/**
 * BPF Engraving Geometry — Phase 5.1.1 (shadow mode foundation).
 *
 * Pure calculation only: given a resolved Engraving Profile, the text
 * to engrave, and a target render size, computes font size (shrink-to-
 * fit), line breaks, per-line positions, the affine transform that maps
 * a flat reference render onto the profile's geometry, and a clip path.
 * It never touches the DOM, never creates a canvas, never draws a
 * pixel — see assets/bpf-engraving-shadow.js for the drawing layer that
 * consumes this module's output.
 *
 * Deliberately dependency-free and environment-agnostic (works under
 * Node for automated verification and under a real browser identically)
 * — text measurement is injected via `measureTextWidth`, never assumed,
 * so this file never needs a real canvas 2D context to be exercised.
 *
 * Rendering technique: Canvas 2D's setTransform is an affine transform
 * — exact for mapping a rectangle onto a parallelogram, an
 * approximation for a genuinely non-parallelogram quadrilateral (a true
 * trapezoid needs a projective transform, which Canvas 2D does not
 * provide natively). The three-point affine derivation below is the
 * standard, well-documented technique for this; see
 * docs/engraving-profiles-README.md's "Rendering technique" section for
 * the full reasoning and the deliberately-deferred grid-subdivision
 * technique that would close the non-parallelogram gap exactly, if a
 * future surface ever needs it.
 */

(function (root, factory) {
  const impl = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = impl;
  } else {
    root.BpfEngravingGeometry = impl;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  function rectangleToCorners(profile) {
    const { anchor, width, height, rotation } = profile;
    const halfW = width / 2;
    const halfH = height / 2;
    const rad = ((rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    function rotate(dx, dy) {
      return {
        x: anchor.x + dx * cos - dy * sin,
        y: anchor.y + dx * sin + dy * cos,
      };
    }

    return {
      topLeft: rotate(-halfW, -halfH),
      topRight: rotate(halfW, -halfH),
      bottomRight: rotate(halfW, halfH),
      bottomLeft: rotate(-halfW, halfH),
    };
  }

  function resolveCorners(profile) {
    if (profile.geometryType === 'rectangle') return rectangleToCorners(profile);
    if (profile.geometryType === 'quadrilateral') return profile.corners;
    throw new Error(`Unknown geometryType "${profile.geometryType}"`);
  }

  // Standard 3-point affine derivation: maps reference-canvas (0,0),
  // (refWidth,0), (0,refHeight) onto three of the target quad's four
  // corners (in target-canvas pixel space). The fourth corner
  // (bottomRight) is implied by the affine mapping, not independently
  // honored — exact when the quad is a parallelogram, approximate
  // otherwise (see module header).
  function deriveAffineTransform(cornersPx, refWidth, refHeight) {
    const { topLeft: p0, topRight: p1, bottomLeft: p2 } = cornersPx;
    const a = (p1.x - p0.x) / refWidth;
    const b = (p1.y - p0.y) / refWidth;
    const c = (p2.x - p0.x) / refHeight;
    const d = (p2.y - p0.y) / refHeight;
    const e = p0.x;
    const f = p0.y;
    return { a, b, c, d, e, f };
  }

  function normalizedToPixels(corners, targetWidth, targetHeight) {
    const out = {};
    for (const key of Object.keys(corners)) {
      const point = corners[key];
      out[key] = point ? { x: point.x * targetWidth, y: point.y * targetHeight } : null;
    }
    return out;
  }

  // Greedy word-wrap up to profile.lineCount lines within
  // maximumRenderWidth, at a given font size. Returns null (not an
  // error) if the text cannot fit within the line budget at all —
  // the caller's shrink-to-fit loop tries a smaller font size next.
  function wrapLines(text, fontSizePx, profile, measureTextWidth) {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return [''];

    const maxWidth = profile.maximumRenderWidth;
    const lineCount = profile.lineCount || 1;

    if (lineCount === 1 || profile.overflowStrategy === 'shrink-to-fit') {
      const single = words.join(' ');
      return measureTextWidth(single, fontSizePx, profile.fontFamily) <= maxWidth ? [single] : null;
    }

    const lines = [];
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (measureTextWidth(candidate, fontSizePx, profile.fontFamily) <= maxWidth) {
        current = candidate;
      } else {
        if (!current) return null; // a single word alone exceeds maxWidth at this font size
        lines.push(current);
        current = word;
        if (lines.length >= lineCount) return null; // ran out of lines before running out of words
      }
    }
    if (current) lines.push(current);
    return lines.length <= lineCount ? lines : null;
  }

  // Iterates font size downward from maximumFontSize to minimumFontSize
  // until the text fits the line budget, per overflowStrategy. Never
  // truncates the string itself — at minimumFontSize, if it still
  // doesn't fit, returns the smallest size tried and flags a warning;
  // clipping (not truncation) is what handles that case downstream.
  function fitText(text, profile, measureTextWidth) {
    const warnings = [];
    for (let size = profile.maximumFontSize; size >= profile.minimumFontSize; size -= 1) {
      const lines = wrapLines(text, size, profile, measureTextWidth);
      if (lines) return { fontSizePx: size, lines, warnings };
    }
    const smallestLines = wrapLines(text, profile.minimumFontSize, profile, measureTextWidth) || [text];
    warnings.push('Text does not fit within minimumFontSize at the given geometry — will be clipped, not truncated.');
    return { fontSizePx: profile.minimumFontSize, lines: smallestLines, warnings };
  }

  function layoutLines(lines, fontSizePx, profile) {
    const lineSpacing = profile.lineSpacing || 1.2;
    const lineHeight = fontSizePx * lineSpacing;
    const totalHeight = lineHeight * lines.length;
    const startY = (profile.maximumRenderHeight - totalHeight) / 2 + lineHeight / 2;

    return lines.map((text, i) => {
      let x;
      if (profile.alignment === 'left') x = 0;
      else if (profile.alignment === 'right') x = profile.maximumRenderWidth;
      else x = profile.maximumRenderWidth / 2; // center (default)
      return { text, x, y: startY + i * lineHeight };
    });
  }

  function computeClipPath(cornersPx, safeInset, targetWidth, targetHeight) {
    if (!safeInset) return cornersPx;
    // Inset each corner toward the quad's own centroid by safeInset,
    // expressed as a fraction of the target canvas's smaller dimension
    // — keeps the inset visually consistent regardless of quad shape.
    const centroid = { x: 0, y: 0 };
    const keys = Object.keys(cornersPx);
    keys.forEach((k) => {
      centroid.x += cornersPx[k].x / keys.length;
      centroid.y += cornersPx[k].y / keys.length;
    });
    const insetPx = safeInset * Math.min(targetWidth, targetHeight);
    const inset = {};
    keys.forEach((k) => {
      const p = cornersPx[k];
      const dx = centroid.x - p.x;
      const dy = centroid.y - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      inset[k] = { x: p.x + (dx / dist) * insetPx, y: p.y + (dy / dist) * insetPx };
    });
    return inset;
  }

  /**
   * Computes everything a renderer needs to draw text for one profile:
   * font size, line breaks and positions (in reference-canvas space),
   * the affine transform mapping that reference canvas onto the
   * target's actual pixel geometry, and a clip path.
   *
   * @param {object} profile - a resolved, fully-calibrated Engraving Profile
   * @param {string} text - the text to engrave
   * @param {number} targetWidth - target canvas pixel width
   * @param {number} targetHeight - target canvas pixel height
   * @param {(text: string, fontSizePx: number, fontFamily: string) => number} measureTextWidth
   */
  // Phase 5.3.0: renderability and production rollout are two different
  // questions, deliberately kept separate — a lesson learned from this
  // gate itself briefly conflating them. "calibrated" means the
  // geometry is real and safe to render; "active" additionally means
  // typography/production sign-off has happened. Neither implies
  // whether any given customer actually SEES engraving — that decision
  // belongs to the Builder (which product, which viewport, which step
  // it's wired into), never to this gate. See docs/engraving-profiles-
  // README.md's Lifecycle section for the full three-state model.
  const RENDERABLE_STATUSES = ['calibrated', 'active'];

  function calculateEngravingLayout(profile, text, targetWidth, targetHeight, measureTextWidth) {
    if (!RENDERABLE_STATUSES.includes(profile.status)) {
      throw new Error(`Cannot render profile "${profile.id}" — status is "${profile.status}", geometry is not yet calibrated`);
    }

    const { fontSizePx, lines: rawLines, warnings } = fitText(text, profile, measureTextWidth);
    const lines = layoutLines(rawLines, fontSizePx, profile);

    const normalizedCorners = resolveCorners(profile);
    const cornersPx = normalizedToPixels(normalizedCorners, targetWidth, targetHeight);
    const transform = deriveAffineTransform(cornersPx, profile.maximumRenderWidth, profile.maximumRenderHeight);
    const clipPath = profile.clipping
      ? computeClipPath(cornersPx, profile.safeInset, targetWidth, targetHeight)
      : null;

    return {
      fontSizePx,
      fontFamily: profile.fontFamily,
      textBaseline: profile.textBaseline,
      alignment: profile.alignment,
      lines,
      transform,
      cornersPx,
      clipPath,
      warnings,
    };
  }

  return { calculateEngravingLayout, deriveAffineTransform, resolveCorners, fitText, wrapLines };
});
